import React from 'react';
import { policyType as policyTypes } from '@ourbranch/lookups';

import {
  dateOfBirthSchema,
  driverInformationSchema,
  homeSchema,
  priorAddressSchema,
  priorCoverageSchema,
  vinSchema
} from 'quote/components/quote-forms/quote-forms.validations';

const linkStyles = { color: '#f89521', textDecoration: 'underline' };
const errorLookup = ({ isAgency, errorType, policyType, googleLink, zillowLink, canClearUWFormRejections }) => {
  return {
    4000: {
      title: 'Driver info error',
      subtitle:
        'We seem to be missing information on one of the applied drivers. Please resubmit the info and try again. If the error continues, contact support.'
    },
    4001: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please try again, and if the problem persists, contact support."
    },
    4002: {
      title: 'Check back later: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Check back later or contact support."
    },
    4003: {
      title: 'Ineligible state',
      subtitle: "We aren't currently writing insurance policies in the applied state.",
      branchPlusEligible: true
    },
    4004: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please try again, and if the problem persists, contact support."
    },
    4005: {
      title: 'Information error',
      subtitle:
        'There was an error in the information provided for this offer. Please check that the full name and address is correct and resubmit to complete the offer.'
    },
    5001: {
      title: 'Update the prior address details and resubmit to get a price',
      why: 'We are missing some applicant details. Adding the prior address may provide remaining details and provide the best price for the member.',
      quoteFormAlertValidationSchema: priorAddressSchema
    },
    5002: {
      title: 'Date of birth required',
      why: "Please provide the applicant's date of birth so we can confirm their identity and complete their offer.",
      quoteFormAlertValidationSchema: dateOfBirthSchema
    },
    5003: {
      title: 'Current auto coverage details required',
      why: "We couldn't find information about the applicant's current auto insurance provider. These details help us confirm their identity and provide the most accurate price. Please include current coverage limits and continuous years of auto coverage to complete the offer.",
      quoteFormAlertValidationSchema: priorCoverageSchema
    },
    5004: {
      title: 'Home details required',
      why: "We're missing some information about the applicant's home. These details help us to provide the most accurate price and coverage options. Please enter home details to complete the offer."
    },
    5005: {
      title: 'VIN required',
      why: "Please enter the applicant's VIN to complete the offer. These details help us confirm their identity and provide the most accurate price."
    },
    5006: {
      title: 'Retry VIN',
      why: 'There was an unknown error in finding your offer. Resubmit the VIN to complete the offer.'
    },
    5008: {
      title: 'Ineligible state',
      subtitle: "We aren't currently writing insurance policies in the applied state.",
      branchPlusEligible: true
    },
    5009: {
      title: 'Ineligible vehicle',
      why: "Due to underwriting guidelines, we're not able to cover the vehicle indicated by this VIN. Try another VIN or proceed without an auto coverage offer."
    },
    5010: {
      title: 'Retry VIN',
      why: "We can't identify that VIN number. Check for typos and try again, or add a different applicant vehicle."
    },
    5011: {
      title: 'Moratorium zip code',
      subtitle:
        "Due to a higher risk for severe weather, we temporarily aren't writing insurance policies in the applied area.",
      branchPlusEligible: true
    },
    5012: {
      title: "Driver's license number required",
      why: "Please enter the applicant's driver's license number to complete the offer. These details help us confirm their identity and provide the most accurate price.",
      quoteFormAlertValidationSchema: driverInformationSchema
    },
    5013: {
      title: 'Invalid address',
      why: "Sorry, we don't allow PO Box addresses. Please provide a physical address for the applicant to complete the offer."
    },
    5014: {
      title: 'Rater error',
      subtitle:
        "Sorry, we're not sure what went wrong, but we can't proceed with an offer with the information provided. Please review the details and resubmit, or contact support if the error continues."
    },
    5015: {
      title: "Driver's license details required",
      why: "We can't verify the primary insured's driving record. These details help us confirm the applicant's identity and provide the most accurate price. Please submit the applicant's driver's license state and number to complete the offer."
    },
    5016: {
      title: 'Additional information required',
      why: "We need more information on the primary applicant to confirm their identity and provide the most accurate price. Please submit the applicant's date of birth, state, and driver's license number."
    },
    5017: {
      title: 'Restricted state',
      subtitle:
        'Branch is currently only writing offers with specific, authorized entities in this state, and otherwise is not selling coverage here.',
      branchPlusEligible: true
    },
    5018: {
      title: 'Setting fees',
      subtitle:
        "There's been an error in finding the correct local premium taxing authority. Check that the submitted address is correct, or contact support if the error continues."
    },
    5019: {
      title: 'Suspicious activity',
      subtitle:
        "While maintaining the security of our services, our system has detected elements of this offer that appear unusual or suspicious. To complete this offer, please resubmit with the applicant's name and address."
    },
    5020: {
      title: 'Information error',
      subtitle:
        "Sorry, we're not sure what went wrong, but we can't proceed with an offer with the information provided. Please review the details and resubmit, or contact support if the error continues."
    },
    5021: {
      title: 'Repeat applicant',
      subtitle:
        "The applicant has been quoted multiple times in a row, which our system doesn't allow to ensure security and accurate offers. Please navigate to a prior offer for this applicant, or resubmit later."
    },
    5022: {
      title: 'History of nonpayment cancellations',
      subtitle:
        'Records show this applicant has had too many prior cancellations due to nonpayment, and Branch is unable to offer them coverage.',
      branchPlusEligible: true
    },
    5023: {
      title: 'Additional address details required',
      why: "We weren't able to locate the applicant's address. Resubmit with the address's latitude and longitude to complete the offer."
    },
    5024: {
      title: 'Retry ZIP code',
      why: "We can't find a rate for this ZIP code, likely due to a typo or this being a non-residential ZIP code. Double check and retry the applicant's ZIP code, and if neither of these is the case, please contact support."
    },
    5025: {
      title: 'Unit number required',
      why: "We need the unit number for the applicant's address. Enter this information in the secondary address field to complete the offer."
    },
    5026: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Resubmit the offer or contact support.",
      branchPlusEligible: true
    },
    5027: {
      title: 'Ineligible zip code',
      subtitle: "We aren't currently writing insurance policies in the applied area.",
      branchPlusEligible: true
    },
    5028: {
      title: 'Disabled affinity code',
      subtitle:
        "Sorry, it seems your affinity code is disabled, or your affinity code is disabled for the offer's state, so you're unable to create offers at this time. Please contact support."
    },
    5029: {
      title: 'Restricted state',
      subtitle:
        "Sorry, it seems you don't have access to create an offer in the applicant's state. If you believe this is in error, contact support.",
      branchPlusEligible: true
    },
    5030: { title: 'Invalid offer', subtitle: 'This offer is too old to update.' },
    5031: {
      title: 'Invalid offer',
      subtitle: 'We are not offering rideshare coverage at this time.',
      branchPlusEligible: true
    },
    5032: {
      title: 'Ineligible to bundle',
      subtitle:
        "Due to one or more of the requested policy types being ineligible, this offer isn't eligible for a bundle.",
      branchPlusEligible: true
    },
    5033: { title: 'Invalid offer', subtitle: 'There are no available options for this offer.' },
    5034: {
      title: 'Excess rejections',
      subtitle: 'This offer returned with an auxiliary auto rate, we are unable to offer a price at this time.'
    },
    5035: {
      title: 'Wait and retry',
      subtitle:
        'Too many offers for this applicant were submitted in a small span of time. Please wait and retry in thirty seconds to complete the offer.'
    },
    5036: {
      title: 'Affinity disabled',
      subtitle:
        'The affinity code on the offer is disabled. If you have access, clear or replace the affinity code. Otherwise, please contact support.'
    },
    5037: {
      title: 'MSRP too high',
      subtitle: "The MSRP of one or more vehicles on this offer does not meet Branch's underwriting guidelines."
    },
    5038: {
      title: 'No rate for the incidents and violations',
      subtitle:
        'We do not have a filed rate for the incidents and violations (claims and motor vehicle violations) for the set of drivers.'
    },
    5039: {
      title: 'No rate for the deductibles',
      subtitle: 'We do not have a filed rate for the deductibles requested. Please try a different set of deductibles.'
    },
    5041: {
      title: 'Quinstreet error'
    },
    10000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this error."
    },
    10009: {
      title: 'Total claims',
      why: "The applied home is less than this state's eligible home age and has had more than the allowed property claims in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) covering the last five years to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE covering the last five years for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10010: {
      title: 'Same-peril claims',
      why: "The applied home has two or more claims of the same type of peril in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) covering the last five years to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE covering the last five years for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10011: {
      title: 'Older home claims',
      why: "The applied home is more than 50 years old and has had more than the allowed number of claims in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) covering the last five years to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE covering the last five years for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10012: {
      title: 'Animal bite claims',
      why: "The applied home has animal bite claim history. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) covering the last five years to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE covering the last five years for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10013: {
      title: 'Suspended license',
      why: "The applied driver has a record of a suspended license in the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, contact support and provide the motor vehicle report (MVR) for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10014: {
      title: 'Disqualifying charge',
      why: "The applied driver has a record of one of the following disqualifying charges within the last 35 months: DWI, reckless driving, vehicular homicide, failure to file required report, fleeing from police, suspended license, drag racing. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send documentation from the DMV (or equivalent) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10015: {
      title: 'Household total incidents',
      why: "The applied driver(s) has a household record of more than the allowed number of total incidents across all drivers on a policy in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send documentation from the DMV (or equivalent) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10016: {
      title: 'Driver total incidents',
      why: "One of the applied drivers exceeds the limit for total incidents in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send documentation from the DMV (or equivalent) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10017: {
      title: 'PIP claims',
      subtitle:
        "The applicant has a recorded number of PIP claims in their claims history that exceeds our underwriting restrictions, so this driver isn't eligible for Branch coverage."
    },
    10018: {
      title: 'Motor vehicle felony',
      why: "The applicant has a record of a motor vehicle felony within the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, please provide motor vehicle report (MVR) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10019: {
      title: 'Water loss claims',
      why: "The applied home has more than one water loss claim in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10020: {
      title: 'Home claims',
      why: "The applied home has one or more home claims in the last three years. Due to the underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10021: {
      title: 'Water loss claims',
      why: "The applied home has more than one water loss claim in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, send a letter of experience (LOE) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    20000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    20001: {
      title: 'Suspended license',
      why: "The applicant has a record of a suspended license in the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, please provide motor vehicle report (MVR) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20002: {
      title: 'Disqualifying charge',
      why: "The applied driver has a record of one of the following disqualifying charges within the last 35 months: DWI, reckless driving, vehicular homicide, failure to file required report, fleeing from police, suspended license, drag racing. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, please provide motor vehicle report (MVR) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20003: {
      title: 'Household total incidents',
      why: "The applied driver(s) has a household record of three or more total incidents across all drivers on a policy in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, provide a letter of experience (LOE) and documentation from the DMV (or equivalent) to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide a letter of experience (LOE) and documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20004: {
      title: 'Driver total incidents',
      why: "One of the applied drivers exceeds the limit for total incidents in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, provide a letter of experience (LOE) and documentation from the DMV (or equivalent) to agencysupport@ourbranch.com for underwriting review. '
        : 'If this is inaccurate, follow your designated support process and provide a letter of experience (LOE) and documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20005: {
      title: 'Motor vehicle felony',
      why: "The applicant has a record of a motor vehicle felony within the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, please provide documentation from the DMV (or equivalent) that covers the last five years to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    50001: {
      title: errorType === 'REJECTION' ? 'VIN required' : 'Submit VIN',
      subtitle:
        errorType === 'REJECTION'
          ? "We couldn't find any vehicles with the provided information. Please resubmit to complete the offer and provide a VIN."
          : undefined,
      why:
        errorType === 'ALERT'
          ? "Please provide a VIN to complete a bundled offer. Or, if there's not an eligible vehicle, resubmit a monoline dwelling policy to proceed for a monoline offer without an auto policy."
          : undefined,
      branchPlusEligible: true,
      canAnyoneRerunOffer: true,
      quoteFormAlertValidationSchema: [
        policyTypes.Auto,
        policyTypes.AutoBundle,
        policyTypes.ARBundle,
        policyTypes.CABundle,
        policyTypes.HABundle
      ].includes(policyType)
        ? vinSchema
        : undefined
    },
    50002: {
      title: 'Excessive square footage',
      subtitle:
        "The applied home exceeds 6000 square feet, which is more than our underwriting constraints. This home isn't eligible for Branch coverage.",
      canBeOverriddenWithPermissionGroup: true
    },
    50003: {
      title: 'Too many occupants',
      why: "The applied home has more than 12 occupants, which exceeds our underwriting constraints. This home isn't eligible for Branch coverage.",
      nextSteps:
        'If the number of occupants in the home is incorrect and the home details tab is accessible, update the resident count and update the offer.',
      canBeOverriddenWithPermissionGroup: true
    },
    50004: {
      title: 'Vehicles per driver',
      why: 'This offer has more than two vehicles per one driver, which exceeds our underwriting constraints.',
      nextSteps:
        'Please review the registered vehicles and drivers on this offer and, if they are incorrect, correct them. Otherwise, this offer is ineligible.',
      policyErrorMessage:
        "The applied policy has a vehicles-per-person ratio that exceeds our underwriting constraints. These vehicles aren't eligible for Branch coverage."
    },
    50005: {
      title: 'Age of home',
      subtitle:
        'The age of this home exceeds our underwriting guidelines for this state. This home is ineligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50006: {
      title: errorType === 'REJECTION' ? 'Not enough home details' : 'Submit home details',
      subtitle:
        errorType === 'REJECTION'
          ? "We couldn't source enough information on this home to accurately calculate a replacement cost. Please resubmit the offer and provide additional home characteristics under property details."
          : undefined,
      why:
        errorType === 'ALERT'
          ? "Please provide home details to complete a bundled offer. Or, if there's not an eligible home, resubmit to proceed for a monoline offer without a home policy."
          : undefined,
      branchPlusEligible: true,
      canAnyoneRerunOffer: true,
      quoteFormAlertValidationSchema: [policyTypes.Home, policyTypes.HABundle, policyTypes.HomeBundle].includes(
        policyType
      )
        ? homeSchema
        : undefined
    },
    50007: {
      title: 'No prior auto coverage',
      why: 'Based on our data sources, we were unable to verify that the applicant has had continuous auto coverage insurance for the required length of time in this state. Due to underwriting constraints, this driver is ineligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If the applicant has had continuous coverage that meets the state underwriting guidelines, please contact Agency Support with documentation for review.'
        : 'If the applicant has had continuous coverage that meets the state underwriting guidelines, please follow your designated support process for review.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50008: {
      title: 'Exterior insulation and finish system',
      why: 'The primary exterior wall type of the applied home appears to be an exterior insulation and finish system (EIFS). Due to underwriting constraints, this home is ineligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this report is no longer accurate, please send supporting documentation to agencysupport@ourbranch.com for review.'
        : 'If this report is no longer accurate, please follow your designated support process for review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50009: {
      title: 'Stilts',
      why: 'The applied home appears to be built on stilts, piers, or another elevated foundation type, which are not eligible foundations for Branch coverage due to underwriting restrictions.',
      nextSteps: isAgency
        ? 'If this report is inaccurate, please send supporting documentation to agencysupport@ourbranch.com for review.'
        : 'If this report is inaccurate, please follow your designated support process to send supporting documentation for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50010: {
      title: 'Wood roof',
      why: 'Our data sources report that the applied home appears to have a wood roof, which is not an eligible roof type for Branch coverage due to underwriting constraints.',
      nextSteps: (
        <>
          Verify if this is accurate by assessing the roof material using an external source, such as
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, and you have access to home details, change the roof type and update the offer to
          resolve.
        </>
      ),
      canBeOverriddenWithPermissionGroup: true
    },
    50011: {
      title: 'Minimum replacement cost unmet',
      why: 'The applied home has an estimated replacement cost under $50,000, which is less than the minimum for our underwriting constraints. This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is inaccurate and you have access to the home details tab, specify all the home characteristics and contact Agency support for review.'
        : 'If this report is inaccurate, please update the home characteristics and follow your designated support process to send supporting documentation for underwriting review.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50012: {
      title: 'Maximum replacement cost exceeded',
      why: (
        <>
          <div>The applied home has one of the following replacement cost estimate (RCE) issues:</div>
          <ul style={{ marginTop: '4px', marginBottom: 0 }}>
            <li>
              The current RCE is $1,250,000 or higher ($850,000 or higher in TX, or $950,000 or higher in OK, GA, and
              PA), exceeding our underwriting limits.
            </li>
            <li>A previous RCE for this home exceeded these limits.</li>
          </ul>
        </>
      ),
      // why: 'The applied home has an estimated replacement cost of $950,000 or higher (in TX, $850,000 or higher), which is more than the maximum for our underwriting constraints. This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? "If the property details are inaccurate, update them in the home details tab. Once the RCE is within Branch's underwriting guidelines contact agencysupport@ourbranch.com for underwriting approval."
        : "If the property details are inaccurate, update them in the home details tab. Once the RCE is within Branch's underwriting guidelines, follow your designated support process for underwriting review.",
      canBeOverriddenWithPermissionGroup: true
    },
    50013: {
      title: 'Fire protection class 10',
      why: 'The applied home is located more than five miles away from a manned fire station and is therefore classified as a fire protection class 10. This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is inaccurate, please provide proof of proximity to a fire department and fire hydrant to agencysupport@ourbranch.com for underwriting review. If the applied home is in Kentucky or Michigan, contact underwriting support to see if this qualifies for an exception.'
        : 'If this is inaccurate, please follow your designated support process to provide proof of proximity to a fire department and fire hydrant for underwriting review. If the applied home is in Kentucky or Michigan, contact underwriting support to see if this qualifies for an exception. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50014: {
      title: 'Fire protection class not found',
      subtitle:
        "We couldn't locate the nearest fire station for the applied home based on the submitted details. Check the address and enter all home characteristics in the fields on the new offer screen and resubmit to complete the offer.",
      branchPlusEligible: true
    },
    50015: {
      title: 'Number of drivers exceeded',
      why: 'This policy lists ten drivers or more, which exceeds what Branch allows per policy. This applicant is not eligible for Branch coverage.',
      nextSteps: 'Contact support if this auto offer should cover less than ten drivers.',
      canAnyoneRerunOffer: true
    },
    50016: {
      title: 'Number of cars exceeded',
      why: 'This policy lists ten vehicles or more, which exceeds what Branch allows per policy. This applicant is not eligible for Branch coverage.',
      nextSteps: 'Contact support if this auto offer should cover less than ten vehicles.',
      canAnyoneRerunOffer: true
    },
    50017: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    50018: {
      title: 'Ineligible coastal location',
      subtitle: "Branch isn't currently writing insurance in this area due to its coastal location."
    },
    50019: {
      title: 'No fireline score',
      subtitle:
        "We can't seem to find a fireline score to assess the applicant's insurance eligibility. Contact support to complete the offer."
    },
    50020: {
      title: 'High fireline score',
      subtitle:
        'The applied home has a fireline score that indicates an elevated wildfire risk. Due to underwriting constraints, this home is not eligible for Branch coverage.'
    },
    50021: {
      title: 'Excessive fire risk',
      subtitle:
        'The applied home has a fireline score that indicates an elevated wildfire risk, and is also located more than five miles from a manned fire station. Due to underwriting constraints, this home is not eligible for Branch coverage.'
    },
    50022: {
      title: 'No distance-to-coast value',
      why: 'We were unable to locate the distance-to-coast value for this property, which prevents us from finding an accurate price for Branch coverage.',
      nextSteps:
        'Confirm the property information, including precise distance from the nearest coast or body of water, and resubmit for an updated offer. If the problem persists, contact support.',
      canAnyoneRerunOffer: true
    },
    50023: {
      title: 'Ineligible coastal location',
      subtitle:
        'The applied home is within a close proximity to tidal water. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50024: {
      title: 'Excessive personal property',
      why: 'The applied home exceeds our designated limit of scheduled personal property (SPP) cost—10% of coverage C. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —10% of coverage C. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50025: {
      title: 'Excessive personal property',
      why: 'The applied home exceeds our designated limit of scheduled personal property (SPP) cost—10% of coverage A. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —10% of coverage A. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50026: {
      title: 'Excessive total personal property',
      why: 'The applied home exceeds our total designated limit of scheduled personal property (SPP) cost—50% of coverage C. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —50% of coverage C. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50027: {
      title: 'No wind or hail exclusion',
      why: 'Branch does not allow wind or hail exclusions for this area. Due to underwriting constrictions, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Resubmit to complete the offer. If this error persists, contact agencysupport@ourbranch.com.'
        : 'Resubmit to complete the offer. If this error persists, follow your designated support process to resolve.',
      canAnyoneRerunOffer: true
    },
    50028: {
      title: 'High fire protection class',
      why: "The applied home has a fire protection class that exceeds Branch's limit of 10. Due to underwriting constraints, this home is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If the applied home is located within 1000 feet of a fire hydrant and within five miles of a manned fire station, this rejection can be cleared by underwriting. If this exception is applicable, please provide proof of proximity to a fire department and fire hydrant to agencysupport@ourbranch.com for underwriting review.'
        : 'If the applied home is located within 1000 feet of a fire hydrant and within five miles of a manned fire station, this rejection can be cleared. If this exception is applicable, please follow your designated support process to provide proof of proximity to a fire department and fire hydrant for underwriting review.'
    },
    50029: {
      title: 'Auto not requested',
      why: 'Auto insurance was not requested with this offer.',
      nextSteps: 'Resubmit the request including auto as a requested property type for a complete offer.',
      canAnyoneRerunOffer: true
    },
    50030: {
      title: 'Roof condition',
      why: 'According to recent aerial footage, the roof of the applied home appears to be in poor condition.',
      nextSteps: isAgency
        ? 'To accurately assess the home and complete the offer, please provide a recent photo of the roof to agencysupport@ourbranch.com for underwriting review.'
        : ' To accurately assess the home and complete the offer, please follow your designated support process to provide a recent photo of the roof for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50031: {
      title: 'Yard debris',
      why: 'According to recent aerial footage, the yard of the applied home appears to contain significant debris. ',
      nextSteps: isAgency
        ? 'To accurately assess the home and complete the offer, please provide a recent photo of the yard(s) to agencysupport@ourbranch.com for underwriting review.'
        : 'To accurately assess the home and complete the offer, please follow your designated support process to provide a recent photo of the yard(s) for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50032: {
      title: 'Coastal location',
      subtitle:
        'The applied home is located in a coastal area with a restricted number of policies, so this home is not eligible for Branch coverage.'
    },
    50033: {
      title: 'Non-continuous coverage',
      why: 'The applied driver has less than the required amount of continuous coverage for this state. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is inaccurate, provide proof of prior coverage to agencysupport@ourbranch.com. '
        : 'If this is inaccurate, follow your designated support process to provide proof of coverage for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50034: {
      title: 'Invalid prior carrier',
      why: "The applicant's prior carrier is invalid. Due to underwriting constraints, this driver is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If the applicant has a valid prior carrier, please provide a declarations page for the member with continuous coverage and no lapse to agencysupport@ourbranch.com for underwriting review.'
        : 'If the applicant has a valid prior carrier, please follow your designated support process to provide a declarations page for the member with continuous coverage and no lapse for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50035: {
      title: 'Invalid incident or violation',
      subtitle:
        'The applicant has more cars than drivers listed. Due to underwriting constraints, the auto offer is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50036: {
      title: 'Vehicle to driver ratio',
      why: 'The applicant has more cars than drivers listed. Due to underwriting constraints, the auto offer is not eligible for Branch coverage.',
      nextSteps:
        'Update the offer with additional drivers or remove any vehicles that are no longer owned by applicant to resolve this rejection.',
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50037: {
      title: 'Invalid bodily injury limit',
      why: 'The applicant has requested a bodily injury limit of less than $50K. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Update the BI limits to a value above $50K to make the auto policy eligible. If this error persists, contact Agency support.'
        : 'Update the BI limits to a value above $50K to make the auto policy eligible. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50038: {
      title: 'Prior coverage not found',
      why: "We weren't able to find details on the applicant's current coverage. Without this information, we are unable to provide an offer for this driver for Branch coverage.",
      nextSteps: isAgency
        ? 'Resubmit the request to receive a new offer. If this error persists, contact agencysupport@ourbranch.com.'
        : 'Resubmit the request to receive a new offer. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50039: {
      title: 'Excluded drivers',
      why: 'The applicant has requested to exclude a driver from their policy. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Resubmit the request to receive a new offer. If this error persists, contact agencysupport@ourbranch.com.'
        : 'Resubmit the request to receive a new offer. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      policyErrorMessage: 'Excluded drivers are not allowed on Branch monoline auto policies',
      branchPlusEligible: true,
      canAnyoneRerunOffer: true
    },
    50040: {
      title: 'Low score / No record',
      subtitle:
        "The applicant has an insurance score that doesn't meet our eligibility requirements, or there is no record of the score. Due to underwriting constraints, this home is not eligible for Branch coverage.",
      canBeOverriddenWithPermissionGroup: true
    },
    50041: {
      title: 'Non-eligible characteristic reported',
      subtitle:
        'The applicant previously reported a home characteristic that is outside of Branch underwriting constraints, so the home is not eligible for Branch coverage.'
    },
    50042: {
      title: 'Non-eligible characteristic reported',
      subtitle:
        'The applicant previously reported an auto characteristic that is outside of Branch underwriting constraints, so the vehicle is not eligible for Branch coverage.'
    },
    50064: {
      title: 'Flat roof',
      why: 'The home characteristics pulled from trusted data sources indicate this home has a flat roof over a living area or, in AZ and NM, has an older flat roof. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'In AZ and NM, if roof age is incorrect, update it on the home details tab. If roof type is incorrect, please contact agencysupport@ourbranch.com for underwriting approval.'
        : 'In AZ and NM, if roof age is incorrect, update it on the home details tab. If roof type is incorrect, please follow your designated support process for underwriting approval. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50083: {
      title: 'Rental history',
      why: "The applicant's condo has a rental history. Due to underwriting constraints, this condo is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If you believe this condo would be covered under an extenuating circumstance, please contact agencysupport@ourbranch.com for underwriting approval. '
        : 'If you believe this condo would be covered under an extenuating circumstance, please follow your designated support process for underwriting approval. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50084: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline home coverage in the applied state. Consider if the applicant could bundle, or Branch can still help the applicant find coverage.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50085: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline condo coverage in the applied state. Consider if the applicant could bundle.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50086: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline auto coverage in the applied state. Consider if the applicant could bundle.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50087: {
      title: 'Primary applicant age',
      subtitle:
        'The primary applicant is younger than 19 years old, and does not meet our eligibility requirements for auto coverage.'
    },
    50088: {
      title: 'Secondary home',
      why: 'The applied home is a secondary home. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is inaccurate and this is a primary home, please update the home details and contact Agency support for review.'
        : 'If this is inaccurate and this is a primary home, please update the home details and follow your designated support process for review.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50089: {
      title: 'Home claims',
      subtitle:
        'The applied home has a number of claims that exceeds our eligibility requirements. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true
    },
    50090: {
      title: 'API Rejection for Home',
      subtitle: "The offer was requested through Branch's API. Please requote to resolve this error."
    },
    50091: {
      title: 'API Rejection for Auto',
      subtitle: "The offer was requested through Branch's API. Please requote to resolve this error."
    },
    50094: {
      title: 'Property replacement cost',
      subtitle:
        'The applied home has a dwelling limit and increased building structure coverage (IBSC) that, combined, exceed our maximum threshold of 1.2M. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'To make the home policy eligible, update the IBSC coverage to reduce the combined dwelling limit and IBSC amount to below the threshold.',
      canAnyoneRerunOffer: true
    },
    50095: {
      title: 'Risk concentration',
      subtitle:
        'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true
    },
    50096: {
      title: errorType === 'REJECTION' ? 'Different property record' : 'Is this property a [type_placeholder]?',
      why:
        errorType === 'REJECTION'
          ? 'Our data indicates the applied [requestedType_placeholder] is a [type_placeholder], which is not eligible for [requestedType_placeholder] coverage.'
          : 'Our data sources indicate that the property at this address is not a [requestedType_placeholder] but rather a [type_placeholder].',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50097: {
      title: errorType === 'REJECTION' ? 'Commercial property' : 'Is this a commercial property?',
      why:
        errorType === 'REJECTION'
          ? 'Our data indicates the applied address is a commercial property in non-residential zoning, which is not eligible for Branch coverage.'
          : 'Our data sources indicate that the property at this address is zoned for commercial use, and Branch does not insure commercial properties in this state. ',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50098: {
      title: errorType === 'REJECTION' ? 'Unsupported property' : 'Is this residence any of the following?',
      subtitle:
        errorType === 'REJECTION' ? undefined : (
          <ul>
            <li>Mobile Home</li>
            <li>Apartment</li>
            <li>Manufactured Home</li>
            <li>Condo</li>
          </ul>
        ),
      why:
        errorType === 'REJECTION'
          ? "The applied home is an unsupported property type (i.e. apartment, mobile home, manufactured home, etc) that isn't eligible for Branch home or condo coverage."
          : 'Our data sources indicate that the property at this address is zoned for non-residential use, and Branch does not insure these types of properties in this state.',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50099: {
      title: 'Need prior auto',
      subtitle:
        'We require prior auto information in order to rate for all policy types. Please resubmit offer with accurate details to provide prior coverage information and clear this rejection.',
      canBeOverriddenWithPermissionGroup: false
    },
    50100: {
      title: 'Invalid AUX symbol',
      why: 'The vehicle was rated with an XX or blank AUX symbol. This rating error needs to be resolved before the auto offer is eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Contact agency support to have the vehicle rating symbols manually assigned.'
        : 'Contact support to have the vehicle rating symbols manually assigned.',
      policyErrorMessage:
        'One of the vehicles on this policy returned with an invalid aux symbol. Please reach out to support to resolve this issue.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50101: {
      title: 'High acreage',
      subtitle:
        'The applied home is on a lot of 25 or more acres. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50102: {
      title: 'Asbestos characteristics',
      why: 'The applied home includes asbestos building materials. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If home has been updated, proof of asbestos removal can be submitted to agencysupport@ourbranch.com for underwriting review.'
        : 'If home has been updated, follow your designated support process to submit proof of asbestos removal for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50103: {
      title: 'Historic neighborhood',
      subtitle:
        'The applied home is a historic property or in a historic neighborhood. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50104: {
      title: 'Salvaged vehicle',
      why: 'The applicant has a vehicle with a branded or salvage title. Due to underwriting constraints, this vehicle is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If you have access to the auto details tab, remove the salvaged vehicle from the auto offer to make the auto offer eligible. If this is an error, proof of the current title can be submitted to agencysupport@ourbranch.com for underwriting review. '
        : 'If you have access to the auto details tab, remove the salvaged vehicle from the auto offer to make the auto offer eligible. If this is an error, follow your designated support process to submit proof of the current title for underwriting review. Or, Branch can still help the applicant find coverage. ',
      policyErrorMessage: 'The added vehicle is ineligible due to a branded title.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50105: {
      title: 'Vacant property',
      why: 'The applied home has been unoccupied (abandoned or vacant) for 15 or more days. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is an error, proof of occupancy (i.e. cable or internet bill) can be submitted to agencysupport@ourbranch.com for underwriting review.'
        : 'If this is an error, follow your designated support process to submit proof of occupancy (i.e. cable or internet bill) for underwriting review. Or, Branch can still help the applicant find coverage. ',
      branchPlusEligible: true,
      anyoneCanOverride: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50106: {
      title: 'Roof materials',
      why: (
        <>
          <div>
            Our data sources report that the applied home has a roof material that is not eligible for Branch coverage.
            These ineligible roof materials include:
          </div>
          <ul style={{ marginTop: '4px', marginBottom: 0 }}>
            <li>Interlocking shingles</li>
            <li>Corrugated roof</li>
            <li>Built-up roof</li>
            <li>Composition rolled roof</li>
            <li>A roof with membrane material</li>
          </ul>
        </>
      ),
      nextSteps: canClearUWFormRejections ? (
        <>
          Verify if this is accurate by assessing the roof material using an external source, such as{' '}
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, you may attest and submit it as inaccurate. Otherwise we can still help the applicant
          find coverage.
        </>
      ) : (
        <>
          Verify if this is accurate by assessing the properties roof material using an external source, such as{' '}
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, you may attest and submit it as inaccurate.
        </>
      ),
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50107: {
      title: 'Foreclosed property',
      why: 'The applied home is currently in foreclosure. Due to underwriting constraints, this home is not eligible for Branch coverage. ',
      nextSteps: isAgency
        ? 'If this is inaccurate, contact agencysupport@ourbranch.com.'
        : 'If this is inaccurate, follow your designated support process for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50108: {
      title: 'Line of business restriction',
      why: "We don't allow this agency or partner to sell this policy type in this state in this system."
    },
    50109: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50110: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50111: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50112: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50113: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50114: {
      title: 'Age of roof',
      why: 'The age of this roof exceeds our underwriting guidelines for this state. This home is ineligible for Branch coverage.',
      nextSteps:
        'If the roof age currently listed on the offer is incorrect, please update to the correct value to remove this rejection.',
      branchPlusEligible: true
    },
    50115: {
      title: 'Weather-related moratorium for ZIP code',
      why: "There is a temporary weather-related moratorium for this offer's ZIP code.",
      nextSteps: isAgency
        ? 'See Agency portal for more details. Once moratorium is lifted, update the offer to remove this underwriting hold.'
        : 'Once the moratorium is lifted, update the offer to remove this underwriting hold',
      hideRerunOffer: true
    },
    50116: {
      title: 'The home’s replacement cost significantly exceeds its purchase price',
      why: 'We cannot insure this home because the calculated estimated replacement cost is more than 300% of its market value.',
      nextSteps: isAgency
        ? 'If this is inaccurate, contact agency support with proof of the recent sale price or purchase date.'
        : 'If the information is incorrect, please update the purchase price, purchase date, and/or home details and follow your designated process',
      canBeOverriddenWithPermissionGroup: true
    },
    50117: {
      title: 'Vehicle MSRP too high',
      why: 'The MSRP of one or more vehicles on this offer does not meet Branch’s underwriting guidelines.',
      nextSteps: isAgency
        ? 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible.'
        : 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible. Or, Branch can still help the applicant find coverage.',
      canBeOverriddenWithPermissionGroup: true
    },
    50118: {
      title: 'Ineligible vehicle(s)',
      why: `Due to Branch's underwriting guidelines, we're not able to cover one or more of the vehicles on the auto offer.`,
      nextSteps: isAgency
        ? 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible.'
        : 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible. Or, Branch can still help the applicant find coverage.',
      canBeOverriddenWithPermissionGroup: true
    },
    50044: {
      title: 'Coverage A Is Less Than 100K',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },

    50045: {
      title: 'Trampolines Present',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50047: {
      title: 'Unconventional Home',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50048: {
      title: 'Ineligible Dog Or Aggression History',
      why: 'Certain dog breeds and aggressive dogs, are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50054: {
      title: 'Skateboard/Bike Ramp, Diving Board, Or Pool Slide Present',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50055: {
      title: 'Property Is Owned By Corporation, LLC, LP, LLP, Estate, Or Partnership',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50057: { title: 'Day Care Operating In Home', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50058: { title: 'Seasonal Or Secondary Home', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50059: {
      title: 'More Than 2 Mortgages',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50061: {
      title: 'Home Age Is Greater Than 40 Years',
      why: 'Based on the age of your home, we require a 4-POINT inspection to be completed and submitted prior to binding. The 4-POINT must have been completed within the past 12 months, and documents the age and condition of electrical, roof, heating and cooling system and plumbing systems. The inspection must be completed by a licensed general or building contractor, building inspector, architect, engineer or a member of the American Society of Home Inspectors (ASHI), the Florida Association of Building Inspectors (FABI) or the National Association of Certified Home Inspectors (NACHI).',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50063: {
      title: 'Ineligible Occupancy',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50066: {
      title: 'Prior Loss Reported In Last 5 Years',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50067: {
      title: 'Two Property Or Liability Losses',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50068: { title: 'No Prior Home Insurance', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50069: {
      title: 'Prior Home Policy Cancelled or Non-Renewed',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50072: {
      title: 'Unconventional Construction Or Defective Materials',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50073: {
      title: 'Wind/Hail Exclusion Requested',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50075: {
      title: 'Lapse In Coverage',
      why: "Please provide the reason why you have had a lapse in auto insurance. \nIf we are not allowing an exception for the lapse 'this does not meet our underwriting guidelines and we are unable to offer you a policy'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50076: {
      title: 'Too Many PIP Claims In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50077: {
      title: 'Too Many At Fault Accidents In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50078: {
      title: 'Too Many Accidents(Regardless of Fault) In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50079: {
      title: 'More Than 2 Infractions In Last 3 years',
      why: 'Drivers with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50080: {
      title: 'Insurance Score Too Low/No Hit',
      why: "Try to rerun insurance score once confirmed spelling of name, DOB, etc. If still no hit and other adverse factors are present advise 'we are unable to provide an auto policy at this time due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50081: {
      title: 'MVR No Hit Or UDR',
      why: "Try to rerun MVR once confirmed spelling of name, DOB, driver's license number, etc. If still no hit and other adverse factors are present advise 'we are unable to provide an auto policy at this time due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50082: {
      title: 'SR22 Or FR44 Required',
      why: 'Potential members with these characteristics are ineligible in our program. If this is a mid term endorsement, this requires a dev escalation. Independent Agents, please reach out to Agency Support to complete this action.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50043: {
      title: 'Water Heater Older Than 15 Years',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50046: {
      title: 'Property Greater Than 5 Acres Or Is PC 9 - 10',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50049: {
      title: 'Prior Sinkhole Activity Or Adjacent To Sinkhole',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50050: {
      title: 'Saddle Animals, Farm, Or Non-Domesticated Animals',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50051: {
      title: 'Open Foundation',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50052: {
      title: 'In Foreclosure Property',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50053: {
      title: 'No Polybutylene Plumbing',
      why: 'Homes with polybutylene plumbing are ineligible for our home program. If you wish to proceed with the offer, we must receive proof of professional replacement prior to binding. Acceptable proof includes permits, completed contract and a signed statement from a licensed plumber that all polybutylene plumbing has been removed from your property.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50056: {
      title: 'Excluded Content Coverage',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50060: { title: 'Commercial Occupation', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50062: {
      title: 'Shingle Or Wood Roof',
      why: "Review risk for any defects - if multiple adverse factors exist on risk, 'we are unable to offer a home policy due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50065: {
      title: 'Tile Or Metal Roof over 25 Years Old',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    }
  };
};

function parseErrorMetadata(error) {
  try {
    return JSON.parse(error.data);
  } catch (e) {
    console.log('error parsing error metadata', e);
    return null;
  }
}

export const getErrorData = ({
  error,
  state,
  isAgency = false,
  errorType = 'REJECTION',
  policyType = undefined,
  googleLink = undefined,
  zillowLink = undefined,
  canClearUWFormRejections = false
}) => {
  let errorData = {
    code: error?.code
  };

  if (
    error?.message === 'Execution timed out.' ||
    error?.message === 'The offer took too long to generate. Please try again or reach out to dev for support.'
  ) {
    return {
      code: error.code,
      title: 'Execution timed out',
      why: `This offer took too long to generate. After 15 seconds, please search for the applicant's name and address in
        the offer search. If the offer does not appear, please retry.`,
      policyErrorMessage: `This offer took too long to generate. After 15 seconds, please search for the applicant's name and address in
        the offer search. If the offer does not appear, please retry.`
    };
  }

  // Parse Basis Theory Proxy errors from Stripe
  if (error && error.message && error.message.includes('Basis Theory Proxy')) {
    try {
      // BT errors seem to come with the message like this:
      // message: "Basis Theory Proxy 400 error proxying https://api.stripe.com/v1/tokens - type:"invalid_request_error" code:"routing_number_invalid" message:"Routing number must have 9 digits""
      const stripeErrorMatch = error.message.match(/message:"([^"]+)"/);

      if (stripeErrorMatch && stripeErrorMatch[1]) {
        errorData.why = stripeErrorMatch[1];
        errorData.title = 'Payment Information Error';
      }
    } catch (e) {
      console.log('Error parsing Basis Theory Proxy error', e);
      errorData.why = 'Error processing payment information';
    }
  } else {
    // If not Basis Theory Proxy error, use raw error.message
    errorData.why = error.message || 'Unknown error occurred';
  }

  if (!error?.code) {
    return errorData;
  }

  // grab all our relevant data from the errorData object for this error code
  if (!isNaN(error.code)) {
    errorData = {
      ...error,
      ...errorLookup({
        state,
        isAgency,
        errorType,
        policyType,
        zillowLink,
        googleLink,
        canClearUWFormRejections
      })[error.code]
    };
  }

  // replace the type placeholder if needed for the title or why
  if (errorData.type && errorData.title && String(errorData.title).includes('[type_placeholder]')) {
    errorData.title = String(errorData.title).replace('[type_placeholder]', errorData.data?.type || errorData.type);
  }
  if (errorData.type && errorData.why && String(errorData.why).includes('[type_placeholder]')) {
    errorData.why = String(errorData.why).replaceAll('[type_placeholder]', errorData.data?.type || errorData.type);
  }
  if (errorData.type && errorData.why && String(errorData.why).includes('[requestedType_placeholder]')) {
    const typeToUse = errorData.data?.type || errorData.type;
    const newPropertyType = typeToUse === 'condo' ? 'home' : 'condo';
    errorData.why = String(errorData.why).replaceAll('[requestedType_placeholder]', newPropertyType);
  }

  if (error.data) {
    const parsedData = parseErrorMetadata(error);
    if (parsedData?.cars) {
      errorData.title = errorData.title + ` for car(s) ${parsedData.cars.join(', ')}`;
    }
  }

  errorData.code = Number(error.code);
  return errorData;
};

export default getErrorData;
